import cx from 'classnames';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';

import {Icon} from 'components';
import {themeConfig} from 'constants/theme';

// Search accepts a pasted invitation link, so the limit must fit a long host plus '?cct=' and a uuid.
const MAX_LENGTH = 256;

type Props = {
    label: string;
    value: string;
    open?: boolean;
    disabled?: boolean;
    className?: string;
    onClick?: () => void;
    onChange: (value: string) => void;
    onSubmit?: () => void;
}

export const Search: React.FC<Props> = ({
    open, label, value, disabled, className, onChange, onSubmit, onClick
}: Props) => {
    const [focus, setFocus] = useState<boolean>(false);
    const [screen, setScreen] = useState<'mobile' | 'desktop'>('desktop');

    const handleFocus = useCallback(() => setFocus(true), []);

    const handleBlur = useCallback(() => setFocus(false), []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.currentTarget.value);
    }, [onChange]);

    // The search is submitted explicitly, so Enter has to work as the keyboard equivalent of the Apply button.
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSubmit?.();
        }
    }, [onSubmit]);

    // On mobile the icon opens and closes the field; on desktop the field is already there, so the magnifier is
    // the most obvious way to run the search and would otherwise do nothing at all.
    const handleIconClick = useCallback(() => {
        if (screen === 'mobile') {
            onClick?.();
        } else {
            onSubmit?.();
        }
    }, [screen, onClick, onSubmit]);

    const handleResize = useCallback(() => {
        const windowWidth = window?.innerWidth;
        const screenType = windowWidth >= themeConfig.screens.lg ? 'desktop' : 'mobile';
        setScreen(screenType);
        if (screenType === 'desktop' && open) {
            onClick?.();
        }
    }, [open, onClick]);

    const isInputVisible = useMemo<boolean>(() => screen === 'desktop' || screen === 'mobile' && open, [screen, open]);

    const isActive = useMemo(() => focus || value, [focus, value]);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    return (
        <div
            className={cx('vs-search tw-relative', className)}
        >
            {isInputVisible &&
                <Fragment>
                    {label &&
                        <label className={cx(`tw-absolute tw-left-0 tw-pl-3 tw-pr-7 tw-w-full tw-text-default-dimmed
                                              tw-text-opacity-60 tw-pointer-events-none tw-box-border tw-whitespace-nowrap
                                              tw-text-ellipsis tw-leading-8 tw-transition-label tw-ease-linear tw-duration-300`, {
                            'tw-text-sm tw--top-6 tw-h-4': isActive,
                            'tw-top-0 tw-h-6': !isActive,
                        })}
                        >
                            {label}
                        </label>}
                    <div className={cx(`tw-w-full tw-inline-flex tw-box-border tw-rounded-none
                                        tw-bg-light tw-border-b tw-border-dark hover:tw-border-primary`, {
                        'tw-border-dark': focus && value,
                        'tw-border-primary': focus,
                        'tw-border-default': disabled,
                    })}
                    >
                        <input
                            className='tw-w-full tw-py-1 tw-pl-3 tw-pr-7 tw-h-8'
                            name='search'
                            type='text'
                            autoComplete='off'
                            maxLength={MAX_LENGTH}
                            disabled={disabled}
                            value={value}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                    </div>
                </Fragment>}
            <Icon
                name={isInputVisible && screen === 'mobile' ? 'close' : 'search'}
                size={isInputVisible ? 'sm' : 'lg'}
                button={screen === 'mobile'}
                color='default-dimmed'
                className={cx('tw-right-2 tw-bottom-2 tw-ml-1', {
                    'tw-absolute': isInputVisible
                })}
                onClick={handleIconClick}
            />
        </div>
    );
};
